import { z } from 'zod';
import { type PageLayout, pageLayoutSchema } from './customization';
import { type FieldDef, OBJECT_NAMES, type ObjectDef, type ObjectName } from './objects';

// ---------------------------------------------------------------------------
// Record types: variants of a standard object (e.g. "New business" vs "Renewal" opportunities).
// Each can limit picklist values, use its own page layout and (opportunities) its own pipeline.
// Every active record type is available to everyone who can create records of the object.
// ---------------------------------------------------------------------------

export const RECORD_TYPES_PER_OBJECT = 20;

export const recordTypeSchema = z
  .object({
    objectName: z.enum(OBJECT_NAMES),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(1000).nullable().optional(),
    isActive: z.boolean().default(true),
    isDefault: z.boolean().default(false),
    /** Picklist field → the values this record type allows (fields left out allow every value). */
    picklists: z.record(z.array(z.string().min(1).max(60)).min(1).max(500)).default({}),
    /** Its own page layout; null uses the object's layout. */
    layout: pageLayoutSchema.nullable().optional(),
    /** Opportunities only: deals of this type use this pipeline. */
    pipelineId: z.string().uuid().nullable().optional(),
  })
  .strict()
  .refine((r) => !r.isDefault || r.isActive, { path: ['isDefault'], message: 'The default record type must be active' })
  .refine((r) => !r.pipelineId || r.objectName === 'opportunity', { path: ['pipelineId'], message: 'Only opportunities have pipelines' });
export type RecordTypeInput = z.infer<typeof recordTypeSchema>;

export interface RecordType extends RecordTypeInput {
  id: string;
  sortOrder: number;
  recordCount: number;
}

/** The parts the web app needs (session), for every record type of the org, active or not. */
export interface RecordTypeInfo {
  id: string;
  objectName: ObjectName;
  name: string;
  isActive: boolean;
  isDefault: boolean;
  picklists: Record<string, string[]>;
  layout: PageLayout | null;
  pipelineId: string | null;
}


/** Problems with a record type's picklist limits against the object's fields. */
export function recordTypeIssues(rt: RecordTypeInput, obj: ObjectDef): string[] {
  const out: string[] = [];
  for (const [field, values] of Object.entries(rt.picklists)) {
    const f = obj.fields.find((x) => x.name === field);
    if (!f || f.type !== 'picklist' || f.readOnly) { out.push(`"${field}" isn't a picklist that people set`); continue; }
    const known = new Set((f.options ?? []).map((o) => o.value));
    const unknown = values.filter((v) => !known.has(v));
    if (unknown.length) out.push(`${f.label}: unknown values ${unknown.join(', ')}`);
  }
  return out;
}

/** Picklist values a field offers under a record type (all of them when the type doesn't limit it). */
export function allowedValues(field: FieldDef, rt: Pick<RecordTypeInfo, 'picklists'> | null | undefined): FieldDef['options'] {
  const allowed = rt?.picklists[field.name];
  return allowed ? field.options?.filter((o) => allowed.includes(o.value)) : field.options;
}
